class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => {
      if (evt.code !== 'Escape') return;
      // Close an open shipping/note popup first; otherwise close the whole drawer.
      if (!this.closeActivePopup()) this.close();
    });
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
  }
  connectedCallback() {
    this.init();

    requestAnimationFrame(() => {
      this.setHeaderCartIconAccessibility();
    });
  }

  init() {
    this.removeEventListener('click', this._delegatedHandler);

    this._delegatedHandler = (e) => {
      const target = e.target;


      if (target.closest('.shipping-btn')) {
        this.shNote();
      }

      if (target.closest('.shipping-item .close-the-daw-upper')) {
        this.shNoteClose();
      }


      if (target.closest('.note-btn')) {
        this.openNote();
      }

      if (target.closest('.note-item .close-the-daw-upper') || target.closest('.apply-btn')) {
        this.closeNote();
      }
    };


    this.addEventListener('click', this._delegatedHandler);
    this.initializeShippingEstimators();
  }

  initializeShippingEstimators() {
    if (typeof window.initializeShippingEstimator !== 'function') return;

    this.querySelectorAll('.shipping-estimator-container').forEach((container) => {
      window.initializeShippingEstimator(container);
    });
  }


  shNote() {
    this.openCartPopup('.shipping-item', '.shipping-btn');
  }

  shNoteClose() {
    this.closeCartPopup('.shipping-item', '.shipping-btn');
  }


  openNote() {
    this.openCartPopup('.note-item', '.note-btn');
  }

  closeNote() {
    this.closeCartPopup('.note-item', '.note-btn');
  }

  // Open a cart-drawer popup (shipping estimate / order note) and confine keyboard
  // focus to it. Without trapping, Tab/Shift+Tab leak to the cart items behind the
  // popup because the drawer's focus trap spans the whole drawer; this also moves
  // focus into the popup on open.
  openCartPopup(popupSelector, triggerSelector) {
    const popup = this.querySelector(popupSelector);
    if (!popup) return;
    this.popupReturnFocus = document.activeElement;
    this.querySelector('.cart-btn-popup')?.classList.add('active');
    popup.classList.add('active');
    const focusTarget =
      popup.querySelector('select:enabled, input:not([type="hidden"]):enabled, textarea:enabled') ||
      popup.querySelector('button') ||
      popup;
    trapFocus(popup, focusTarget);
  }

  // Close a cart-drawer popup, restore the drawer's focus trap, and return focus
  // to the button that opened it.
  closeCartPopup(popupSelector, triggerSelector) {
    const popup = this.querySelector(popupSelector);
    if (!popup) return;
    popup.classList.remove('active');
    this.querySelector('.cart-btn-popup')?.classList.remove('active');
    // Return focus to whatever opened the popup; fall back to the trigger button
    // (if rendered) or the drawer dialog so focus is never lost.
    const returnTo =
      this.popupReturnFocus && this.popupReturnFocus.isConnected && this.contains(this.popupReturnFocus)
        ? this.popupReturnFocus
        : this.querySelector(triggerSelector) || this.querySelector('.drawer__inner');
    trapFocus(document.getElementById('CartDrawer'), returnTo);
  }

  // Close whichever popup is open (so Escape dismisses the popup before the drawer).
  // Returns true if a popup was open.
  closeActivePopup() {
    if (this.querySelector('.shipping-item.active')) {
      this.shNoteClose();
      return true;
    }
    if (this.querySelector('.note-item.active')) {
      this.closeNote();
      return true;
    }
    return false;
  }


  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;


    if (cartLink.hasAttribute('data-cart-drawer-setup')) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.setAttribute('data-cart-drawer-setup', 'true');

    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });

    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }


  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) {
      this.setSummaryAccessibility(cartDrawerNote);
    }

    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        // The close button lives in the always-visible header (outside .drawer__inner-empty),
        // so trap focus on the whole #CartDrawer in both states. Trapping only on
        // .drawer__inner-empty strands keyboard focus on "Continue shopping" with no way to
        // Tab to the close button when the cart is empty.
        const containerToTrapFocusOn = document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    // Reset any open popup so it doesn't linger (and strand focus) on reopen.
    this.querySelector('.shipping-item')?.classList.remove('active');
    this.querySelector('.note-item')?.classList.remove('active');
    this.querySelector('.cart-btn-popup')?.classList.remove('active');
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute(
        'aria-expanded',
        !event.currentTarget.closest('details').hasAttribute('open')
      );
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }


  renderContents(parsedState) {
    this.classList.contains('is-empty') && this.classList.remove('is-empty');

    this.productId = parsedState.id;

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;

      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });


    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.open();
    this.init();
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      { id: 'cart-drawer', selector: '#CartDrawer' },
      { id: 'cart-icon-bubble' },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  connectedCallback() {
    super.connectedCallback();
    const cartDrawer = this.closest('cart-drawer');
    if (typeof cartDrawer?.initializeShippingEstimators === 'function') {
      cartDrawer.initializeShippingEstimators();
    }
  }

  getSectionsToRender() {
    return [
      { id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

document.addEventListener('shopify:section:load', () => {
  const drawer = document.querySelector('cart-drawer');
  drawer?.setHeaderCartIconAccessibility();
});

document.addEventListener('shopify:section:reorder', () => {
  const drawer = document.querySelector('cart-drawer');
  drawer?.setHeaderCartIconAccessibility();
});
